import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MapContainer, Polyline, Popup, TileLayer, Marker, Circle } from 'react-leaflet';
import L from 'leaflet';
import styles from './CarGpsTab.module.scss';
import { useTranslation } from 'react-i18next';
import TrackingService from 'api/tracking/TrackingService';
import { FaCalendarAlt, FaRegClock, FaMapMarkerAlt, FaGasPump, FaHourglassEnd, FaClock, FaFileExport, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import GpsExportModal from './components/GpsExportModal/GpsExportModal';
import DriverHistoryModal from './components/DriverHistoryModal/DriverHistoryModal';
import { Col, Row } from 'react-flexbox-grid';
import FormItem from 'common/components/formItem/FormItem';
import DateTimePicker from 'common/components/dateTimePicker/DateTimePicker';
import { DATEPICKER_FORMAT_DEFAULT, DATE_TIME_FORMAT_DEFAULT, DEFAULT_LANGUAGE, STORAGE } from 'Config';
import InputGroup from 'common/components/inputGroup/InputGroup';
import Storage from 'common/services/Storage';
import moment from 'moment';
import TrackingRoutes from 'api/tracking/models/TrackingRoutes';
import DateFormat from 'common/components/dateFormat/dateFormat';
import GaugeIcon from 'assets/svg/gauge.svg';
import DistanceFormat from 'common/components/distanceFormat/DistanceFormat';
import { addressFormat, useGeoLocator } from 'common/services/GeoLocator';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import { TrackingItemDto } from 'api/tracking/models/TrackingItemDto';
import RoutingMachine from 'common/components/maps/RoutineMachine';
import WaypointsClusterLayer from 'common/components/maps/WaypointsClusterLayer';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import CurrentLocation from 'screens/vehicles-gps/components/currentLocation/CurrentLocation';
import SortingDropdown from 'common/components/sortingDropdown/SortingDropdown';
import ReactDOMServer from 'react-dom/server';
import EcoDriving from 'screens/vehicles-gps/components/ecoDriving/EcoDriving';
import ReactTooltip from 'react-tooltip';
import MarkerPopup from 'screens/vehicles-gps/components/markerPopup/MarkerPopup';
import RecenterMap from 'screens/vehicles-gps/components/recenterMap/RecenterMap';
import NumberFormat from 'react-number-format';
import { TrackingRouteSearchCriteria } from 'api/tracking/models/TrackingRouteSearchCriteria';
import Loading from 'common/services/Loading';
import { useHistory, useParams } from 'react-router-dom';
import DriveIcon from 'assets/svg/drive.svg';
import GeofencesService from 'api/geofences/GeofencesService';
import { LocationArea } from 'common/components/maps/DrawTools';
import { markerIcon } from 'screens/geofence/GeofenceScreen';
import { GeofenceDto } from 'api/geofences/models/GeofenceDto';

// Helper components (stable, memoized)
type GeoLocatorItemProps = {
    id: string;
    latitude: number;
    longitude: number;
    fuelLevel: number;
    icon: string;
    date?: Date;
    isLast: boolean;
    tooltipMessage?: string;
    index?: number;
    speed: number;
    address?: string | null;
    odometer?: number;
};

// Formata o odómetro com separador de milhares por espaço e 2 casas decimais (ex: 270000 -> "270 000,00").
// Arredonda como o MoneyFormat/react-number-format do header (round half-up correto), isolando a parte
// decimal a partir do toString — evita o bug do toFixed em números grandes, onde (320307.055).toFixed(2)
// daria "320307.05" quando o header (correto) mostra "320307.06".
const formatOdometer = (value: number) => {
    const neg = value < 0;
    const [intRaw, decRaw = ''] = Math.abs(value).toString().split('.');
    let frac = Math.round(parseFloat('0.' + (decRaw || '0')) * 100);
    let intNum = parseInt(intRaw || '0', 10);
    if (frac >= 100) { intNum += 1; frac = 0; } // carry (ex.: ,999 -> ,00 e +1 ao inteiro)
    const decStr = frac.toString().padStart(2, '0');
    const intStr = intNum.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return `${neg ? '-' : ''}${intStr},${decStr}`;
};

const GeoLocatorItem: React.FC<GeoLocatorItemProps> = React.memo(({
    id, latitude, longitude, fuelLevel, icon, date, isLast, tooltipMessage, index, speed, address, odometer
}) => {
    const { t } = useTranslation();
    const tooltipContent = (tooltipMessage && odometer && odometer > 0)
        ? `${tooltipMessage} · ${formatOdometer(odometer)} ${t('common.kms')}`
        : tooltipMessage;
    let formatedAddress = '';
    if (address) {
        formatedAddress = address;
    } else {
        // useGeoLocator hook inside component
        const { isLoading, error, data } = useGeoLocator(latitude, longitude);
        if (isLoading) return <LoadingSpinner />;
        if (error) console.error('An error occurred while fetching the user data ', error);
        formatedAddress = addressFormat(data, latitude, longitude);
        if (data?.display_name) {
            try {
                TrackingService.updateGpsAddress(id, formatedAddress, isLast).catch(console.error);
            } catch (err) {
                console.log('Couldn\'t update route address', err);
            }
        }
    }

    return (
        <div className={styles.inputGroup} data-tip={tooltipContent ? tooltipContent : undefined} data-for={`${tooltipMessage + '_' + index}`}>
            <div className={icon}><FaMapMarkerAlt /></div>
            {!isLast && <div className={styles.iconLine}></div>}
            <div className={styles.input}>
                <span className={styles.labelAddress}>{formatedAddress}</span>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {date && <div className={styles.labelDate}><DateFormat format={DATE_TIME_FORMAT_DEFAULT} value={date} /></div>}
                    {isLast && (speed > 0) && <div className={styles.moving}>
                        <div>{t('location.in_transit')}</div>
                    </div>}
                    <div className={styles.infoFuel}>
                        {fuelLevel > 0 && <><FaGasPump /><NumberFormat className={styles.labelFuel} displayType='text' decimalScale={0} suffix='%' value={fuelLevel} /></>}
                    </div>
                </div>
            </div>
            <ReactTooltip id={`${tooltipMessage + '_' + index}`} />
        </div>
    );
});
GeoLocatorItem.displayName = 'GeoLocatorItem';

type GeoRoutesProps = {
    idx: number;
    id: string;
    start: number;
    stop: number;
    startLatitude: number;
    startLongitude: number;
    stopLatitude: number;
    stopLongitude: number;
    startDate: Date;
    stopDate: Date;
    startKm: number;
    stopKm: number;
    startFuelLevel: number;
    stopFuelLevel: number;
    ecoValue: number;
    idling: number;
    speed: number;
    startAddress?: string | null;
    stopAddress?: string | null;
    driverName?: string;
    resolvedDriverSource?: string;
    resolvedSourceEntityId?: string;
    hasDriverHistory?: boolean;
    identificationTagId?: string;
    identificationTagHasCurrentDriver?: boolean;
    selectedRouteKey?: string;
    onSelect: (id: string, start: number, stop: number) => void;
    calcTimeTotalRoute: (startDate: Date, stopDate: Date) => string;
    calcIdlingTotalRoute: (idling: number) => string;
};

const GeoRoutes: React.FC<GeoRoutesProps> = React.memo(props => {
    const {
        idx, id, start, stop,
        startLatitude, startLongitude, stopLatitude, stopLongitude,
        startDate, stopDate, startKm, stopKm,
        startFuelLevel, stopFuelLevel, ecoValue, idling, speed,
        startAddress, stopAddress, driverName, resolvedDriverSource, resolvedSourceEntityId, hasDriverHistory,
        identificationTagId, identificationTagHasCurrentDriver, selectedRouteKey, onSelect, calcTimeTotalRoute, calcIdlingTotalRoute
    } = props;
    const { t } = useTranslation();
    const history = useHistory();
    const [showDriverHistory, setShowDriverHistory] = useState(false);

    const kms = stopKm - startKm;
    const isSelected = selectedRouteKey === `georoute_${id}_${start}_${stop}`;

    const handleClick = useCallback(() => {
        onSelect(id, start, stop);
    }, [onSelect, id, start, stop]);

    const handleDriverClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        setShowDriverHistory(true);
    }, []);

    const handleTagClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        if (identificationTagId) {
            history.push(`/settings/identificationTags/edit/${identificationTagId}`);
        }
    }, [identificationTagId, history]);

    return (
        <div className={`${styles.card} ${isSelected ? styles.selected : ''}`} onClick={handleClick} id={`route-${id}`} role="button" tabIndex={0}>
            <div className={styles.cardBody}>
                <GeoLocatorItem id={id} latitude={startLatitude} longitude={startLongitude} fuelLevel={startFuelLevel} icon={styles.sourcePin} date={startDate} isLast={false} tooltipMessage={t('location.info_messages.origin') as string} index={idx} speed={speed} address={startAddress} odometer={startKm} />
                <GeoLocatorItem id={id} latitude={stopLatitude} longitude={stopLongitude} fuelLevel={stopFuelLevel} icon={styles.destinationPin} date={stopDate} isLast={true} tooltipMessage={t('location.info_messages.destination') as string} index={idx} speed={speed} address={stopAddress} odometer={stopKm} />
            </div>
            <div className={styles.cardFooter}>
                <div className={`${styles.spaceBetween} ${styles.flexWrap}`}>
                    <EcoDriving value={ecoValue} idx={idx} />
                    <div className={styles.badge} data-tip={t('location.info_messages.distance') as string} data-for={`distance_${idx}`}>
                        <div className={`${styles.icon}`} style={{ backgroundImage: 'url(' + GaugeIcon + ')' }} />
                        <div><DistanceFormat value={kms} showLessThan1Km={false} /></div>
                    </div>
                    <ReactTooltip id={`distance_${idx}`} />
                    <div className={styles.badge} data-tip={t('location.info_messages.time') as string} data-for={`time_${idx}`}>
                        <div className={styles.mr}><FaRegClock /></div>
                        <div>{calcTimeTotalRoute(startDate, stopDate)}</div>
                    </div>
                    <ReactTooltip id={`time_${idx}`} />
                    <div className={styles.badge} data-tip={t('location.info_messages.idling') as string} data-for={`idling_${idx}`}>
                        <div className={styles.mr}><FaHourglassEnd /></div>
                        <div>{calcIdlingTotalRoute(idling)}</div>
                    </div>
                    <ReactTooltip id={`idling_${idx}`} />
                    {(driverName || hasDriverHistory || resolvedDriverSource === 'ANONYMIZED') && <>
                        <div className={`${styles.badge} ${styles.driver}`} data-tip={t('location.info_messages.driver') as string} data-for={`driver_${idx}`} onClick={handleDriverClick} style={{ cursor: 'pointer' }}>
                            <div className={`${styles.icon}`} style={{ backgroundImage: 'url(' + DriveIcon + ')' }} />
                            <div>{resolvedDriverSource === 'ANONYMIZED' ? t('privacy.anonymized_label') as string : driverName}</div>
                        </div>
                        <ReactTooltip id={`driver_${idx}`} />
                    </>}
                    {!(driverName || hasDriverHistory) && identificationTagId && <>
                        <div className={`${styles.badge} ${styles.driver}`} data-tip={t((identificationTagHasCurrentDriver ? 'location.info_messages.tag_without_driver_at_date_tooltip' : 'location.info_messages.tag_without_driver_tooltip') as any) as string} data-for={`tag_driver_${idx}`} onClick={handleTagClick} style={{ cursor: 'pointer' }}>
                            <div className={`${styles.icon}`} style={{ backgroundImage: 'url(' + DriveIcon + ')' }} />
                            <div>{t((identificationTagHasCurrentDriver ? 'location.info_messages.tag_without_driver_at_date' : 'location.info_messages.tag_without_driver') as any) as string}</div>
                        </div>
                        <ReactTooltip id={`tag_driver_${idx}`} />
                    </>}
                </div>
            </div>
            <DriverHistoryModal
                isOpen={showDriverHistory}
                onClose={() => setShowDriverHistory(false)}
                tripId={id}
                driverName={driverName}
                resolvedDriverSource={resolvedDriverSource}
                resolvedSourceEntityId={resolvedSourceEntityId}
            />
        </div>
    );
});
GeoRoutes.displayName = 'GeoRoutes';

// Main component 
type Props = {
    vehicleId: string;
    tourReRender: () => void;
}

const CarGpsTab: React.FC<Props> = ({ vehicleId, tourReRender }) => {
    const { t } = useTranslation();
    const maxZoom = 18;
    const { tripDate, tripId, geofenceId } = useParams<{ tripDate: string, tripId: string, geofenceId: string }>();
    const [routes, setRoutes] = useState<TrackingRoutes[]>([]);
    const locale = Storage.get(STORAGE.CURRENT_LOCALE) || DEFAULT_LANGUAGE;
    const [position, setPosition] = useState<TrackingItemDto | undefined>(undefined);
    const [waypoints, setWaypoints] = useState<L.LatLng[]>([]);
    const [zoom, setZoom] = useState<number>(12);
    const [selectedRouteKey, setSelectedRouteKey] = useState<string>('');
    const [waypointsInfo, setWaypointsInfo] = useState<TrackingItemDto[]>([]);
    const [waypointsLoaded, setWaypointsLoaded] = useState(false);
    const [refresh, setRefresh] = useState<number>(1);
    const redOptions = { color: 'red' };
    const [showPoints, setShowPoints] = useState<boolean>(Storage.get('SHOWROUTEPOINTS') === 'true');
    const [loaded, setLoaded] = useState(false);
    const [positionLoaded, setPositionLoaded] = useState(false);
    const [daysWithRoutes, setDaysWithRoutes] = useState<Date[]>([]);
    const [geofenceMarkerPosition, setGeofenceMarkerPosition] = useState<L.LatLng | null>(null);
    const [area, setArea] = useState<LocationArea | undefined>(undefined);
    const [geofenceGetted, setGeofenceGetted] = useState<GeofenceDto | undefined>(undefined);
    const [showExportModal, setShowExportModal] = useState<boolean>(false);

    const mapDivRef = useRef<HTMLDivElement | null>(null);
    const routesListDivRef = useRef<HTMLDivElement | null>(null);
    const hasLoadedTripOnce = useRef<boolean>(false);

    const loadingWaypointsRef = useRef<boolean>(false); // lock to avoid duplicate loadWaypoints calls

    const [criteria, setCriteria] = useState<TrackingRouteSearchCriteria>({
        itemsPerPage: 1000,
        page: 1,
        vehicleId: vehicleId,
        date: tripDate ? new Date(tripDate) : new Date(),
        orderColumn: 'start_date',
        orderBy: localStorage.getItem('ROUTESFILTERORDERBY') ?? 'DESC'
    });

    const calcTimeTotalRoute = useCallback((startDate: Date, stopDate: Date) => {
        const diffInTime = new Date(stopDate).getTime() - new Date(startDate).getTime();
        const hours = Math.floor(diffInTime / (1000 * 60 * 60));
        const minutes = Math.floor((diffInTime - (hours * 1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.round((diffInTime - (hours * 3600 * 1000) - (minutes * 1000 * 60)) / 1000);
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}  h`;
    }, []);

    const calcIdlingTotalRoute = useCallback((idling: number) => {
        const hours = Math.floor(idling / 3600);
        const minutes = Math.floor((idling - (hours * 3600)) / 60);
        const seconds = Math.round(idling - (hours * 3600) - (minutes * 60));
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')} h`;
    }, []);

    const loadWaypoints = useCallback(async (id: string, start: number, stop: number) => {
        if (loadingWaypointsRef.current) return;
        // if already selected same route, avoid reloading
        const candidateKey = `georoute_${id}_${start}_${stop}`;
        if (candidateKey === selectedRouteKey) return;

        loadingWaypointsRef.current = true;
        try {
            Loading.show();
            const _waypoints = await TrackingService.getGpsWaypoints(id);
            const wpts: L.LatLng[] = [];
            if (_waypoints && _waypoints.length > 0) {
                _waypoints.forEach(i => {
                    wpts.push(L.latLng(i.latitude ?? 0, i.longitude ?? 0));
                });
            }
            setSelectedRouteKey(candidateKey);
            setWaypointsInfo(_waypoints ?? []);
            setWaypoints(wpts);
            // only bump refresh if waypoints changed
            setRefresh(prev => prev + 1);
            setWaypointsLoaded(true);

            if (tripId !== id) {
                setArea(undefined);
                setGeofenceMarkerPosition(null);
            }
            else if (geofenceGetted) {
                const _geofence = geofenceGetted;
                if (_geofence.latitude && _geofence.longitude && _geofence.radius) {
                    setArea({ latitude: _geofence.latitude, longitude: _geofence.longitude, mRadius: _geofence.radius, radius: _geofence.radius, name: _geofence.name });
                    setGeofenceMarkerPosition(L.latLng(_geofence.latitude ?? 0, _geofence.longitude ?? 0));
                }
            }
        } catch (err) {
            console.error('loadWaypoints error', err);
        } finally {
            loadingWaypointsRef.current = false;
            Loading.hide();
        }
    }, [selectedRouteKey]);

    const handleSelectRoute = useCallback((id: string, start: number, stop: number) => {
        loadWaypoints(id, start, stop);
    }, [loadWaypoints]);

    const [privacyStatus, setPrivacyStatus] = useState<import('api/tracking/TrackingService').PrivacyStatus | null>(null);

    useEffect(() => {
        getData();
        // Check privacy status for selected date
        TrackingService.getPrivacyStatus(vehicleId, (criteria.date ?? new Date()).toISOString())
            .then(setPrivacyStatus)
            .catch(() => setPrivacyStatus(null));
    }, [criteria]);

    useEffect(() => {
        if (!tripId || !routesListDivRef.current) return;

        // Espera o DOM renderizar a lista
        const timeout = setTimeout(() => {
            const selector = `#route-${tripId}`;
            const target = routesListDivRef.current!.querySelector(selector);
            if (target) {
                (target as HTMLElement).scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            } else {
                console.warn('Elemento não encontrado para scroll:', selector);
            }
        }, 50);

        return () => clearTimeout(timeout);
    }, [loaded]);

    const getData = async () => {
        try {
            if (loaded) {
                Loading.show(); //#49402
            }
            const _routes = await TrackingService.getGpsTrips(criteria);
            setRoutes(_routes);
            await getTripDays();
            await getPosition();

            if (tripId && !hasLoadedTripOnce.current) {
                const routeToShow = _routes.find(x => x.id === tripId);
                if (routeToShow) {
                    loadWaypoints(routeToShow.id, routeToShow.start, routeToShow.stop);
                } else {
                    setWaypointsLoaded(true);
                }
                hasLoadedTripOnce.current = true;
            } else {
                setSelectedRouteKey('');
                setWaypoints([]);
                setWaypointsInfo([]);
                setWaypointsLoaded(true);
            }

            if (geofenceId) {
                try {
                    const _geofence = await GeofencesService.getById(geofenceId);
                    if (_geofence && _geofence.latitude && _geofence.longitude && _geofence.radius) {
                        setGeofenceGetted(_geofence);
                        setArea({ latitude: _geofence.latitude, longitude: _geofence.longitude, mRadius: _geofence.radius, radius: _geofence.radius, name: _geofence.name });
                        setGeofenceMarkerPosition(L.latLng(_geofence.latitude ?? 0, _geofence.longitude ?? 0));
                    }
                } catch (err) {
                    console.error('Failed to load geofence', err);
                }
            }

            setLoaded(true);
            setRefresh(prev => prev + 1);
        } finally {
            if (loaded) {
                Loading.hide(); //#49402
            }
            tourReRender();
        }
    };

    const getPosition = async () => {
        try {
            if (loaded) {
                Loading.show(); //#49402
            }
            const positionResult = await TrackingService.getGpsVehiclesPosition([vehicleId]);
            if (positionResult && positionResult.length > 0) {
                const pos = positionResult[0];
                if (pos.latitude && pos.longitude) {
                    setPosition(pos);
                }
            }
            setPositionLoaded(true);
        } catch (err) {
            console.error('getPosition error', err);
        } finally {
            if (loaded) {
                Loading.hide(); //#49402
            }
        }
    };

    const getTripDays = async () => {
        try {
            if (loaded) {
                Loading.show(); //#49402
            }
            const result = await TrackingService.getTripDays(vehicleId);
            setDaysWithRoutes(result.map((x: any) => new Date(x.startDate)));
        } catch (err) {
            console.error('getTripDays error', err);
        } finally {
            if (loaded) {
                Loading.hide(); //#49402
            }
        }
    };

    const calculateZoom = useCallback((distance?: number) => {
        if (!distance) {
            return;
        }
        let z = 15 + Math.round(Math.log2(1 / distance));
        const minZoom = 5;
        if (z > maxZoom) {
            z = maxZoom;
        } else if (z < minZoom) {
            z = minZoom;
        }
        setZoom(z);
    }, []);

    const renderInfo = useCallback((i: number) => {
        if (!waypointsInfo[i]) return <></>;
        return <MarkerPopup position={waypointsInfo[i]} isRoute={i === 0 ? 'END' : (i === waypointsInfo.length - 1 ? 'START' : 'NO')} />;
    }, [waypointsInfo]);

    const renderInfoGeonfence = useCallback(() => {
        if (!geofenceMarkerPosition) return <></>;

        //const urlDetails = `geofences/details/${geofenceId}`;
        return <div className={styles.containerInfoGeofence}>
            {area && area?.name && <div className={styles.itemInfoGeofence}>
                <span>{t('geofences.title')}</span>
                <strong>{area?.name}</strong>
            </div>}
            <div className={styles.itemInfoGeofence}>
                <span>{t('location.latitude')}</span>
                <strong><NumberFormat displayType='text' decimalScale={6} value={geofenceMarkerPosition.lat} /></strong>
            </div>
            <div className={styles.itemInfoGeofence}>
                <span>{t('location.longitude')}</span>
                <strong><NumberFormat displayType='text' decimalScale={6} value={geofenceMarkerPosition.lng} /></strong>
            </div>
            {area && area?.radius && <div className={styles.itemInfoGeofence}>
                <span>{t('geofences.radius')}</span>
                <strong><NumberFormat displayType='text' decimalScale={6} suffix={t('geofences.meters_min')} value={area?.radius} /></strong>
            </div>}
        </div>;
    }, [geofenceMarkerPosition]);

    const loadIconColor = useCallback((i: number) => {
        return i === 0 ? '#dd1021' : (i === waypointsInfo.length - 1 ? '#1bd44d' : '#1781A1');
    }, [waypointsInfo.length]);

    const renderGeofencerMarker = useCallback(() => {
        return geofenceMarkerPosition ? <Marker position={geofenceMarkerPosition} icon={markerIcon}>
            <Popup minWidth={210} closeButton={false}>
                {renderInfoGeonfence()}
            </Popup>
        </Marker> : <></>
    }, [geofenceMarkerPosition]);

    const moveToCurrent = () => {
        setSelectedRouteKey('');
        setWaypointsInfo([]);
        setWaypoints([]);
        setRefresh(prev => prev + 1);
    }

    const customIcon = new L.DivIcon({
        html: ReactDOMServer.renderToString(<FaMapMarkerAlt className={`${position?.ignition ? styles.pinIgnitionOn : styles.pinIgnitionOff}`} style={{ fontSize: '30px' }} />),
        iconAnchor: [15, 30],
        popupAnchor: [1, -34],
        className: styles.pin
    });

    const renderMap = () => {
        // choose zoomPosition (center)
        const zoomPosition = (waypointsInfo && waypointsInfo.length > 0) ? waypointsInfo[Math.floor(waypointsInfo.length / 2)] : position;
        if (position && zoomPosition) {
            return (
                <MapContainer style={{ width: '100%', height: '100%' }} center={[zoomPosition.latitude ?? 0, zoomPosition.longitude ?? 0]}
                    maxZoom={maxZoom} minZoom={5} zoom={zoom} scrollWheelZoom={true} tap={false}>
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    {!(waypointsInfo && waypointsInfo.length > 0) && position && (
                        <Marker position={[position.latitude ?? 0, position.longitude ?? 0]} icon={customIcon}>
                            <Popup minWidth={180} closeButton={false}>
                                <MarkerPopup position={position} isRoute='NO' />
                            </Popup>
                        </Marker>
                    )}

                    {renderGeofencerMarker()}
                    {area && <Circle center={[area.latitude, area.longitude]} radius={area.mRadius}></Circle>}

                    <RoutingMachine
                        waypoints={waypoints}
                        icon={customIcon}
                        hide={true}
                        renderInfo={renderInfo}
                        loadIconColor={loadIconColor}
                    />
                    {showPoints && (
                        <WaypointsClusterLayer
                            waypoints={waypoints}
                            renderInfo={renderInfo}
                        />
                    )}
                    <Polyline pathOptions={redOptions} positions={waypoints} />
                    <RecenterMap lat={zoomPosition.latitude} lng={zoomPosition.longitude} zoom={zoom} />
                </MapContainer>
            );
        } else {
            return <></>;
        }
    };

    const handleShowPoints = (option: boolean) => {
        setShowPoints(option);
        setRefresh(prev => prev + 1);
        Storage.set('SHOWROUTEPOINTS', `${option}`);
    };

    if (!loaded) {
        return <div className={styles.noDataContainer}>
            <LoadingSpinner />
        </div>
    }

    if (!position && loaded && positionLoaded) {
        return <div className={styles.noDataContainer}>
            {t('location.info_messages.no_data')}
        </div>
    }

    const calcDownTime = (startDate: Date, stopDate: Date) => {
        const diffInTime = new Date(stopDate).getTime() - new Date(startDate).getTime();
        const hours = Math.floor(diffInTime / (1000 * 60 * 60));
        const minutes = Math.floor((diffInTime - (hours * 1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.round((diffInTime - (hours * 3600 * 1000) - (minutes * 1000 * 60)) / 1000);
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}  h`;
    }

    const calcTimeTotalRoutes = (__routes: TrackingRoutes[]) => {
        const diffInTime = __routes.map(r => new Date(r.stopDate).getTime() - new Date(r.startDate).getTime()).reduce((p, c) => p + c, 0);
        const hours = Math.floor(diffInTime / (1000 * 60 * 60));
        const minutes = Math.floor((diffInTime - (hours * 1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.round((diffInTime - (hours * 3600 * 1000) - (minutes * 1000 * 60)) / 1000);
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}  h`;
    }

    const calcPenaltyTotalRoutes = (__routes: TrackingRoutes[]) => {
        const kms = __routes.map(r => r.stopKm - r.startKm).reduce((p, c) => p + c, 0);
        const penalty = __routes.map(r => r.penalty).reduce((p, c) => p + c, 0);
        return kms > 0 ? (penalty / kms) : penalty;
    }

    const calcIdlingTotalRoutes = (__routes: TrackingRoutes[]) => {
        const all = __routes.map(r => r.idling).reduce((p, c) => p + c, 0);
        const hours = Math.floor(all / 3600);
        const minutes = Math.floor((all - (hours * 3600)) / 60);
        const seconds = Math.round(all - (hours * 3600) - (minutes * 60));
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')} h`;
    }

    const calcDownTimeTotalRoutes = (__routes: TrackingRoutes[]) => {
        const all = __routes.map((r, i) => (criteria.orderBy === 'ASC' && i > 0) ?
            (new Date(r.startDate).getTime() - new Date(__routes[i - 1].stopDate).getTime()) :
            (criteria.orderBy === 'DESC' && i < __routes.length - 1) ?
                (new Date(r.startDate).getTime() - new Date(__routes[i + 1].stopDate).getTime()) : 0)
            .reduce((p, c) => p + c, 0) / 1000;
        const hours = Math.floor(all / 3600);
        const minutes = Math.floor((all - (hours * 3600)) / 60);
        const seconds = Math.round(all - (hours * 3600) - (minutes * 60));
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')} h`;
    }

    const moveToCurrentHandler = () => {
        moveToCurrent();
    }

    const renderStopTime = (routesArr: TrackingRoutes[], i: number) => {
        return (<>
            {(((criteria.orderBy === 'ASC') && i > 0)) && <div className={`${styles.card}`}>
                <div className={styles.cardBody}>
                    <div className={`${styles.spaceBetween} ${styles.flexWrap}`}>
                        <div> {t('location.info_messages.total_inactive')} </div>
                        <div className={styles.badge} data-tip={t('location.info_messages.total_inactive')} data-for="total_idling">
                            <div className={styles.mr}><FaClock /></div>
                            <div>{calcDownTime(routesArr[i - 1].stopDate, routesArr[i].startDate)}</div>
                        </div>
                    </div>
                </div>
            </div>
            }
            {(((criteria.orderBy === 'DESC') && i < routesArr.length - 1)) && <div className={`${styles.card}`}>
                <div className={styles.cardBody}>
                    <div className={`${styles.spaceBetween} ${styles.flexWrap}`}>
                        <div> {t('location.info_messages.total_inactive')}</div>
                        <div className={styles.badge} data-tip={t('location.info_messages.total_inactive')} data-for="total_idling">
                            <div className={styles.mr}><FaClock /></div>
                            <div>{calcDownTime(routesArr[i + 1].stopDate, routesArr[i].startDate)}</div>
                        </div>
                    </div>
                </div>
            </div>
            }
        </>);
    }

    const isSameDay = (d1: Date, d2: Date) =>
        d1.getDate() === d2.getDate() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getFullYear() === d2.getFullYear();

    const renderDayContents = (day: number, date: Date) => {
        const isSpecial = daysWithRoutes.some(d => isSameDay(d, date));
        return (
            <div style={{ color: isSpecial ? '#16E0CC' : undefined }}>
                <span>{day}</span>
            </div>
        );
    };

    return (
        <div className={styles.container}>
            <Row>
                <Col xs={12} md={4}>
                    <FormItem>
                        <div className={styles.dateNav}>
                            <button
                                type="button"
                                className={styles.dateNavBtn}
                                aria-label={t('location.previous_day') as string}
                                data-tip={t('location.previous_day')}
                                data-for="previous_day"
                                onClick={() => setCriteria(prev => ({ ...prev, date: moment(prev.date).subtract(1, 'day').toDate() }))}
                            >
                                <FaChevronLeft />
                            </button>
                            <ReactTooltip id="previous_day" />
                            <div className={styles.dateNavPicker}>
                                <DateTimePicker
                                    renderDayContents={renderDayContents}
                                    popperClassName={styles.onFg}
                                    name="date"
                                    dateFormat={DATEPICKER_FORMAT_DEFAULT}
                                    locale={locale}
                                    autoComplete='off'
                                    selected={moment(criteria.date).toDate()}
                                    placeholderText={t('common.date')}
                                    onChange={(date: Date) => { setCriteria(prev => ({ ...prev, date })); }}
                                    maxDate={moment(new Date()).toDate()}
                                    customInput={<InputGroup icon={<FaCalendarAlt />} />}
                                />
                            </div>
                            <button
                                type="button"
                                className={styles.dateNavBtn}
                                aria-label={t('location.next_day') as string}
                                data-tip={t('location.next_day')}
                                data-for="next_day"
                                disabled={moment(criteria.date).isSameOrAfter(moment(), 'day')}
                                onClick={() => setCriteria(prev => ({ ...prev, date: moment(prev.date).add(1, 'day').toDate() }))}
                            >
                                <FaChevronRight />
                            </button>
                            <ReactTooltip id="next_day" />
                        </div>
                    </FormItem>
                    {routes && routes.length > 0 ?
                        <Col>
                            <div className={`${styles.card}`}>
                                <div className={styles.cardBody}>
                                    <div className={`${styles.spaceBetween} ${styles.flexWrap}`}>
                                        <EcoDriving value={calcPenaltyTotalRoutes(routes)} idx={'all'} />
                                        <div className={styles.badge} data-tip={t('location.info_messages.total_distance')} data-for="total_distance">
                                            <div className={`${styles.icon}`} style={{ backgroundImage: 'url(' + GaugeIcon + ')' }} />
                                            <div><DistanceFormat value={routes.map(r => r.stopKm - r.startKm).reduce((p, c) => p + c, 0)} showLessThan1Km={false} /></div>
                                        </div>
                                        <ReactTooltip id="total_distance" />
                                        <div className={styles.badge} data-tip={t('location.info_messages.total_time')} data-for="total_time">
                                            <div className={styles.mr}><FaRegClock /></div>
                                            <div>{calcTimeTotalRoutes(routes)}</div>
                                        </div>
                                        <ReactTooltip id="total_time" />
                                        <div className={styles.badge} data-tip={t('location.info_messages.total_idling')} data-for="total_idling">
                                            <div className={styles.mr}><FaHourglassEnd /></div>
                                            <div>{calcIdlingTotalRoutes(routes)}</div>
                                        </div>
                                        <ReactTooltip id="down_time" />
                                        <div className={styles.badge} data-tip={t('location.info_messages.total_inactive')} data-for="down_time">
                                            <div className={styles.mr}><FaClock /></div>
                                            <div>{calcDownTimeTotalRoutes(routes)}</div>
                                        </div>
                                        <ReactTooltip id="total_idling" />
                                    </div>
                                </div>
                            </div>
                            <div className={styles.totalContainer}>
                                <small className={styles.totalText}>
                                    {routes.map(_ => 1).reduce((p, c) => p + c, 0)} {t('location.path')}
                                </small>
                                <div className={styles.toggleContainer} data-tip={t('location.info_messages.show_points')} data-for="showPoints">
                                    <small className={styles.toggleLabel}>{t('location.show_points')}</small>
                                    <label className={`${styles.toggleSwitch}`}>
                                        <input
                                            type='checkbox'
                                            checked={showPoints ? true : false}
                                            onChange={e => handleShowPoints(e.target.checked)}
                                        />
                                        <span className={styles.switch} />
                                    </label>
                                    <ReactTooltip id="showPoints" />
                                </div>
                                <SortingDropdown
                                    onSortChange={(orderBy: string) => { setCriteria(prev => ({ ...prev, orderBy })); localStorage.setItem('ROUTESFILTERORDERBY', orderBy); }}
                                    defaultOption={criteria.orderBy}
                                    closeDropOnSelect={true}
                                />
                            </div>
                            <div className={styles.geoLocation} ref={routesListDivRef}>
                                {routes.map((route, i) => (
                                    <div key={route.id}>
                                        {criteria.orderBy === 'ASC' && renderStopTime(routes, i)}
                                        {route.startLatitude && route.startLongitude && route.stopLatitude && route.stopLongitude && (
                                            <GeoRoutes
                                                idx={i}
                                                key={route.id}
                                                id={route.id}
                                                start={route.start}
                                                stop={route.stop}
                                                startLatitude={route.startLatitude}
                                                startLongitude={route.startLongitude}
                                                stopLatitude={route.stopLatitude}
                                                stopLongitude={route.stopLongitude}
                                                startDate={route.startDate}
                                                stopDate={route.stopDate}
                                                startKm={route.startKm}
                                                stopKm={route.stopKm}
                                                startFuelLevel={route.startFuelLevel}
                                                stopFuelLevel={route.stopFuelLevel}
                                                ecoValue={route.ecoValue}
                                                idling={route.idling}
                                                speed={route.stopSpeed}
                                                startAddress={route.startAddress}
                                                stopAddress={route.stopAddress}
                                                driverName={route.driverName}
                                                resolvedDriverSource={route.resolvedDriverSource}
                                                resolvedSourceEntityId={route.resolvedSourceEntityId}
                                                hasDriverHistory={route.hasDriverHistory}
                                                identificationTagId={route.identificationTagId}
                                                identificationTagHasCurrentDriver={route.identificationTagHasCurrentDriver}
                                                selectedRouteKey={selectedRouteKey}
                                                onSelect={handleSelectRoute}
                                                calcTimeTotalRoute={calcTimeTotalRoute}
                                                calcIdlingTotalRoute={calcIdlingTotalRoute}
                                            />
                                        )}
                                        {criteria.orderBy === 'DESC' && renderStopTime(routes, i)}
                                    </div>
                                ))}
                            </div>
                        </Col>
                        :
                        <Col>
                            <div className={`${styles.emptyContainer}`}>
                                {t('location.info_messages.without_routes')}
                            </div>
                        </Col>}
                </Col>
                <Col xs={12} md={8}>
                    <Row>
                        <Col xs={12} className={styles.currentLocationWrapper}>
                            <div className={styles.currentLocationContainer} onClick={moveToCurrentHandler}>
                                <CurrentLocation position={position} privacyStatus={privacyStatus ?? undefined} />
                            </div>
                            <span className={styles.exportButtonWrapper} data-tip={t('location.export_routes')} data-for="export_routes">
                                <button className={styles.exportButton} onClick={() => { ReactTooltip.hide(); setShowExportModal(true); }}>
                                    <FaFileExport />
                                </button>
                                <ReactTooltip id="export_routes" />
                            </span>
                        </Col>
                        <Col xs={12}>
                            <div className={styles.map} key={`map_${refresh}`} ref={mapDivRef}>
                                {waypointsLoaded && renderMap()}
                            </div>
                        </Col>
                    </Row>
                </Col>
            </Row>

            <GpsExportModal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                vehicleId={vehicleId}
                daysWithRoutes={daysWithRoutes}
            />
        </div>
    );
}

export default CarGpsTab;
